import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { Janela, Sobreposicao } from '@/componentes/JanelaDeAcao';
import { formatarReais, interpretarNumero } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import { apiDasIntegracoes, useLigacoes } from './api';
import {
  quemLancaOTitulo,
  retornoDaBaixa,
  retornoDaNota,
  retornoDoCancelamentoDoTitulo,
  retornoDoTitulo,
  type MestreDaConta,
  type RespostaDaBaixa,
  type RespostaDoTitulo,
  type TituloDaIntegracao,
} from './integracoes';
import { EscolhaDoRegistro } from './JanelasDasIntegracoes';
import { usePermissoesDaIntegracao } from './permissoes';

/**
 * As janelas dos títulos e das notas na mesa (0108): o lançamento do título
 * (sete campos, na gaveta, p.29), a baixa e o cancelamento (na janela, p.30) e
 * a referência da nota em contingência (na gaveta). Com o mestre de fora, o
 * formulário diz quem é o mestre e pede o motivo da contingência — e só para
 * quem tem a licença dela; o banco recusa o resto (regra I6).
 */

function Recusa({ erro }: { readonly erro: unknown }) {
  const f = explicarFalha(erro);
  return (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
      <span className="txt-corpo text-tinta">{f.erro}</span>
      <span className="txt-apoio text-tinta-secundaria">{f.comoResolver}</span>
    </div>
  );
}

function useInvalidar() {
  const cliente = useQueryClient();
  return () => {
    void cliente.invalidateQueries({ queryKey: ['integracoes'] });
    void cliente.invalidateQueries({ queryKey: ['financeiro', 'titulos'] });
    void cliente.invalidateQueries({ queryKey: ['orcamento'] });
  };
}

/** O aviso de quem é o mestre, e o campo da contingência quando ele é de fora. */
function MestreEContingencia({ mestre, rotulo, motivo, aoMudar }: {
  readonly mestre: MestreDaConta | undefined; readonly rotulo: string; readonly motivo: string; readonly aoMudar: (v: string) => void;
}) {
  const pode = usePermissoesDaIntegracao();
  const quem = quemLancaOTitulo(mestre, rotulo);
  return (
    <>
      <p className={`txt-apoio text-pretty ${quem.externo ? 'text-atencao-texto' : 'text-tinta-secundaria'}`}>{quem.frase}</p>
      {quem.externo && pode.contingencia ? (
        <CampoDoMotivo rotulo="Motivo da contingência" valor={motivo} aoMudar={aoMudar} minimo={15} maximo={400} exemplo="O ERP está fora do ar desde a manhã de hoje" />
      ) : null}
      {quem.externo && !pode.contingencia ? (
        <p className="txt-apoio text-pretty text-tinta-secundaria">A escrita em contingência é do perfil Administrador.</p>
      ) : null}
    </>
  );
}

/** O pedido (a pagar) ou a cobrança (a receber) a que o título se liga. */
function EscolhaDaLigacao({ tipo, valor, aoEscolher }: { readonly tipo: 'a_pagar' | 'a_receber'; readonly valor: string; readonly aoEscolher: (id: string) => void }) {
  const [busca, setBusca] = useState('');
  const ligacoes = useLigacoes(tipo, busca, true);
  const lista = ligacoes.data?.ligacoes ?? [];
  const rotulo = tipo === 'a_pagar' ? 'Pedido de compra' : 'Cobrança da medição';
  return (
    <div className="flex flex-col gap-2">
      <CampoTexto rotulo={`Buscar ${rotulo.toLowerCase()} · opcional`} valor={busca} aoMudar={setBusca} placeholder="Número ou parte dele" maxLength={60} />
      {ligacoes.isPending ? (
        <ControleCarregando oQue={`Buscando ${rotulo.toLowerCase()}`} />
      ) : (
        <CampoSelecao
          rotulo={rotulo}
          valor={valor}
          aoMudar={aoEscolher}
          rotuloVazio="Sem ligação"
          opcoes={lista.map((l) => ({ valor: l.id, rotulo: `${l.numero} · ${l.detalhe}` }))}
        />
      )}
    </div>
  );
}

/* ============================================================ o titulo */

export function GavetaDoTitulo({ aberta, aoFechar, mestres, hoje }: {
  readonly aberta: boolean;
  readonly aoFechar: () => void;
  readonly mestres: Readonly<Record<'titulo_a_pagar' | 'titulo_a_receber', MestreDaConta>> | undefined;
  readonly hoje: string;
}) {
  const base = useId();
  const invalidar = useInvalidar();
  const [tipo, setTipo] = useState<'a_pagar' | 'a_receber'>('a_pagar');
  const [numero, setNumero] = useState('');
  const [contraparte, setContraparte] = useState('');
  const [emitidoEm, setEmitidoEm] = useState(hoje);
  const [vencimento, setVencimento] = useState(hoje);
  const [valor, setValor] = useState('');
  const [ligacao, setLigacao] = useState('');
  const [motivo, setMotivo] = useState('');
  const mestre = mestres?.[tipo === 'a_pagar' ? 'titulo_a_pagar' : 'titulo_a_receber'];
  const externo = quemLancaOTitulo(mestre, '').externo;
  const lancar = useMutation({
    mutationFn: (d: Parameters<typeof apiDasIntegracoes.lancarTitulo>[0]) => apiDasIntegracoes.lancarTitulo(d),
    meta: {
      retorno: retornoDe<RespostaDoTitulo>((r) => ({ titulo: r.origem === 'contingencia' ? 'Título lançado em contingência' : 'Título lançado', ...retornoDoTitulo(r) })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    lancar.reset();
    setNumero('');
    setContraparte('');
    setEmitidoEm(hoje);
    setVencimento(hoje);
    setValor('');
    setLigacao('');
    setMotivo('');
    aoFechar();
  }
  const v = interpretarNumero(valor);
  const falta = !numero.trim() || !contraparte || !emitidoEm || !vencimento || v === null || v <= 0 || vencimento < emitidoEm || (externo && motivo.trim().length < 15);
  return (
    <Sobreposicao
      lugar="direita"
      titulo="Lançar título"
      descricao="O título a pagar ou a receber, com o vencimento. Ligado ao pedido ou à cobrança, o caixa do orçamento não conta os dois."
      aberta={aberta}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao
            nivel="primario"
            disabled={lancar.isPending || falta}
            onClick={() => {
              if (v === null) return;
              lancar.mutate({
                tipo, numero: numero.trim(), contraparteId: contraparte, emitidoEm, vencimento, valor: Math.round(v * 100) / 100,
                ligacaoId: ligacao || null, ...(externo ? { contingencia: motivo.trim() } : {}),
              });
            }}
          >
            {lancar.isPending ? 'Lançando…' : externo ? 'Lançar em contingência' : 'Lançar título'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {lancar.isError ? <Recusa erro={lancar.error} /> : null}
        <CampoSelecao
          rotulo="Tipo"
          valor={tipo}
          aoMudar={(x) => { setTipo(x as 'a_pagar' | 'a_receber'); setContraparte(''); setLigacao(''); }}
          opcoes={[{ valor: 'a_pagar', rotulo: 'A pagar' }, { valor: 'a_receber', rotulo: 'A receber' }]}
        />
        <MestreEContingencia mestre={mestre} rotulo={tipo === 'a_pagar' ? 'títulos a pagar' : 'títulos a receber'} motivo={motivo} aoMudar={setMotivo} />
        <CampoTexto rotulo="Número" valor={numero} aoMudar={setNumero} placeholder="NF 4512/1" maxLength={60} />
        <EscolhaDoRegistro entidade={tipo === 'a_pagar' ? 'fornecedor' : 'cliente'} valor={contraparte} aoEscolher={setContraparte} />
        <div className="grid grid-cols-2 gap-4">
          <Campo id={`${base}-emissao`} rotulo="Emissão">
            <input id={`${base}-emissao`} type="date" className="controle w-full" value={emitidoEm} onChange={(e) => { setEmitidoEm(e.target.value); }} />
          </Campo>
          <Campo id={`${base}-vencimento`} rotulo="Vencimento" erro={vencimento && emitidoEm && vencimento < emitidoEm ? 'O vencimento vem antes da emissão. Confira as duas datas.' : null}>
            <input id={`${base}-vencimento`} type="date" className="controle w-full" value={vencimento} onChange={(e) => { setVencimento(e.target.value); }} />
          </Campo>
        </div>
        <CampoTexto rotulo="Valor (R$)" valor={valor} aoMudar={setValor} placeholder="1.320,50" maxLength={16} />
        <EscolhaDaLigacao tipo={tipo} valor={ligacao} aoEscolher={setLigacao} />
      </div>
    </Sobreposicao>
  );
}

/* ============================================================== a baixa */

export function JanelaDaBaixa({ alvo, aoFechar, mestre, hoje }: {
  readonly alvo: TituloDaIntegracao | null; readonly aoFechar: () => void; readonly mestre: MestreDaConta | undefined; readonly hoje: string;
}) {
  const base = useId();
  const invalidar = useInvalidar();
  const [pagoEm, setPagoEm] = useState(hoje);
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const externo = quemLancaOTitulo(mestre, '').externo;
  const baixar = useMutation({
    mutationFn: (d: { id: string; pagoEm: string; valor: number; contingencia?: string }) => apiDasIntegracoes.baixar(d.id, { pagoEm: d.pagoEm, valor: d.valor, ...(d.contingencia ? { contingencia: d.contingencia } : {}) }),
    meta: { retorno: retornoDe<RespostaDaBaixa>((r) => ({ titulo: r.origem === 'contingencia' ? 'Baixa registrada em contingência' : 'Baixa registrada', ...retornoDaBaixa(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    baixar.reset();
    setPagoEm(hoje);
    setValor('');
    setMotivo('');
    aoFechar();
  }
  const v = interpretarNumero(valor);
  const falta = !pagoEm || v === null || v <= 0 || (alvo !== null && v > alvo.emAberto + 0.004) || (externo && motivo.trim().length < 15);
  return (
    <Janela
      titulo={alvo ? `Registrar baixa do título ${alvo.numero}` : 'Registrar baixa'}
      descricao={alvo ? `Em aberto: ${formatarReais(alvo.emAberto)}. A soma das baixas não passa do valor do título.` : ''}
      aberta={alvo !== null}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao
            nivel="primario"
            disabled={baixar.isPending || falta}
            onClick={() => { if (alvo && v !== null) baixar.mutate({ id: alvo.id, pagoEm, valor: Math.round(v * 100) / 100, ...(externo ? { contingencia: motivo.trim() } : {}) }); }}
          >
            {baixar.isPending ? 'Registrando…' : 'Registrar baixa'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {baixar.isError ? <Recusa erro={baixar.error} /> : null}
        <MestreEContingencia mestre={mestre} rotulo="baixas" motivo={motivo} aoMudar={setMotivo} />
        <Campo id={`${base}-pago`} rotulo="Dia do pagamento ou do recebimento">
          <input id={`${base}-pago`} type="date" className="controle w-full" value={pagoEm} onChange={(e) => { setPagoEm(e.target.value); }} />
        </Campo>
        <CampoTexto rotulo="Valor (R$)" valor={valor} aoMudar={setValor} placeholder={alvo ? formatarReais(alvo.emAberto).replace('R$ ', '') : ''} maxLength={16} />
      </div>
    </Janela>
  );
}

/* ========================================================= o cancelamento */

export function JanelaDoCancelamentoDoTitulo({ alvo, aoFechar, mestre }: { readonly alvo: TituloDaIntegracao | null; readonly aoFechar: () => void; readonly mestre: MestreDaConta | undefined }) {
  const invalidar = useInvalidar();
  const [motivo, setMotivo] = useState('');
  const [contingencia, setContingencia] = useState('');
  const externo = quemLancaOTitulo(mestre, '').externo;
  const cancelar = useMutation({
    mutationFn: (d: { id: string; motivo: string; contingencia?: string }) => apiDasIntegracoes.cancelarTitulo(d.id, { motivo: d.motivo, ...(d.contingencia ? { contingencia: d.contingencia } : {}) }),
    meta: { retorno: retornoDe<{ numero: string }, { motivo: string }>((r, v) => ({ titulo: 'Título cancelado', ...retornoDoCancelamentoDoTitulo(r, v.motivo) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    cancelar.reset();
    setMotivo('');
    setContingencia('');
    aoFechar();
  }
  return (
    <Janela
      titulo={alvo ? `Cancelar o título ${alvo.numero}?` : 'Cancelar o título?'}
      descricao="O título sai da posição e do caixa. Título com baixa não se cancela: o pagamento aconteceu."
      aberta={alvo !== null}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Manter título</Botao>
          <Botao
            nivel="destrutivo"
            disabled={cancelar.isPending || motivo.trim().length < 15 || (externo && contingencia.trim().length < 15)}
            onClick={() => { if (alvo) cancelar.mutate({ id: alvo.id, motivo: motivo.trim(), ...(externo ? { contingencia: contingencia.trim() } : {}) }); }}
          >
            {cancelar.isPending ? 'Cancelando…' : 'Cancelar título'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {cancelar.isError ? <Recusa erro={cancelar.error} /> : null}
        <MestreEContingencia mestre={mestre} rotulo="títulos" motivo={contingencia} aoMudar={setContingencia} />
        <CampoDoMotivo valor={motivo} aoMudar={setMotivo} minimo={15} maximo={400} exemplo="Lançado em duplicidade: o certo é o NF 4512/1" />
      </div>
    </Janela>
  );
}

/* ============================================================== a nota */

export function GavetaDaNota({ aberta, aoFechar, mestres, hoje }: {
  readonly aberta: boolean;
  readonly aoFechar: () => void;
  readonly mestres: Readonly<Record<'nota_de_entrada' | 'nota_de_saida', MestreDaConta>> | undefined;
  readonly hoje: string;
}) {
  const base = useId();
  const invalidar = useInvalidar();
  const [tipo, setTipo] = useState<'entrada' | 'saida'>('entrada');
  const [numero, setNumero] = useState('');
  const [serie, setSerie] = useState('');
  const [chave, setChave] = useState('');
  const [emitidaEm, setEmitidaEm] = useState(hoje);
  const [valor, setValor] = useState('');
  const [contraparte, setContraparte] = useState('');
  const [ligacao, setLigacao] = useState('');
  const [motivo, setMotivo] = useState('');
  const mestre = mestres?.[tipo === 'entrada' ? 'nota_de_entrada' : 'nota_de_saida'];
  const registrar = useMutation({
    mutationFn: (d: Parameters<typeof apiDasIntegracoes.registrarNota>[0]) => apiDasIntegracoes.registrarNota(d),
    meta: {
      retorno: retornoDe<{ numero: string; chaveAcesso: string; valor: number; tipo: 'entrada' | 'saida' }>((r) => ({ titulo: 'Referência da nota registrada', ...retornoDaNota(r) })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    registrar.reset();
    setNumero('');
    setSerie('');
    setChave('');
    setEmitidaEm(hoje);
    setValor('');
    setContraparte('');
    setLigacao('');
    setMotivo('');
    aoFechar();
  }
  const digitos = chave.replace(/\D/g, '');
  const v = interpretarNumero(valor);
  const falta = !mestre?.mestre || !numero.trim() || digitos.length !== 44 || !emitidaEm || v === null || v < 0 || !contraparte || motivo.trim().length < 15;
  return (
    <Sobreposicao
      lugar="direita"
      titulo="Registrar referência de nota"
      descricao="Só a referência da nota que o mestre emitiu — número, chave de acesso, valor e data —, em contingência. A plataforma não emite nota nem apura imposto."
      aberta={aberta}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao
            nivel="primario"
            disabled={registrar.isPending || falta}
            onClick={() => {
              if (v === null) return;
              registrar.mutate({
                tipo, numero: numero.trim(), ...(serie.trim() ? { serie: serie.trim() } : {}), chaveAcesso: digitos, emitidaEm,
                valor: Math.round(v * 100) / 100, contraparteId: contraparte, ligacaoId: ligacao || null, contingencia: motivo.trim(),
              });
            }}
          >
            {registrar.isPending ? 'Registrando…' : 'Registrar referência'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {registrar.isError ? <Recusa erro={registrar.error} /> : null}
        <CampoSelecao
          rotulo="Tipo"
          valor={tipo}
          aoMudar={(x) => { setTipo(x as 'entrada' | 'saida'); setContraparte(''); setLigacao(''); }}
          opcoes={[{ valor: 'entrada', rotulo: 'Nota de entrada' }, { valor: 'saida', rotulo: 'Nota de saída' }]}
        />
        <p className={`txt-apoio text-pretty ${mestre?.mestre ? 'text-tinta-secundaria' : 'text-atencao-texto'}`}>
          {mestre?.mestre
            ? `O mestre das notas é ${mestre.mestre}. A referência fica em contingência até o lote dele casar pela chave de acesso.`
            : 'O fiscal nunca é da plataforma: aprove na matriz o sistema de fora que é o mestre das notas antes de registrar a referência.'}
        </p>
        <div className="grid grid-cols-[minmax(0,1fr)_96px] gap-4">
          <CampoTexto rotulo="Número" valor={numero} aoMudar={setNumero} placeholder="4512" maxLength={20} />
          <CampoTexto rotulo="Série · opcional" valor={serie} aoMudar={setSerie} placeholder="1" maxLength={5} />
        </div>
        <CampoTexto
          rotulo="Chave de acesso"
          valor={chave}
          aoMudar={setChave}
          placeholder="44 dígitos do DANFE"
          maxLength={60}
          ajuda={`${String(digitos.length)} de 44 dígitos. O último é conferido pelos outros 43.`}
        />
        <div className="grid grid-cols-2 gap-4">
          <Campo id={`${base}-emissao`} rotulo="Emissão">
            <input id={`${base}-emissao`} type="date" className="controle w-full" value={emitidaEm} onChange={(e) => { setEmitidaEm(e.target.value); }} />
          </Campo>
          <CampoTexto rotulo="Valor (R$)" valor={valor} aoMudar={setValor} placeholder="1.320,50" maxLength={16} />
        </div>
        <EscolhaDoRegistro entidade={tipo === 'entrada' ? 'fornecedor' : 'cliente'} valor={contraparte} aoEscolher={setContraparte} />
        <EscolhaDaLigacao tipo={tipo === 'entrada' ? 'a_pagar' : 'a_receber'} valor={ligacao} aoEscolher={setLigacao} />
        <CampoDoMotivo rotulo="Motivo da contingência" valor={motivo} aoMudar={setMotivo} minimo={15} maximo={400} exemplo="O ERP está fora do ar e a nota chegou com a mercadoria" />
      </div>
    </Sobreposicao>
  );
}
